import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import Header from './Header';
import { useNavigate } from 'react-router-dom';
import { FaChevronDown, FaChevronUp, FaEdit, FaTrash } from 'react-icons/fa';
import api from './services/apiService';
import TrainingCard from './components/training/TrainingCard';
import './scss/matches.scss';
import './scss/training.scss';
import { authService } from './services/authService';
import ProFeatureModal from './components/ProFeatureModal';

const TrainingSchedule = () => {
  const [loading, setLoading] = useState(true);
  const [trainingSessions, setTrainingSessions] = useState([]);
  const [showPastTraining, setShowPastTraining] = useState(false); // false = upcoming, true = past
  const [viewMode, setViewMode] = useState('team'); // 'my' or 'team'
  const [showProModal, setShowProModal] = useState(false);
  const [showDummyData, setShowDummyData] = useState(false);
  const [repeatSessions, setRepeatSessions] = useState([]);
  const [showRepeatAccordion, setShowRepeatAccordion] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingRepeat, setEditingRepeat] = useState(null);
  const [totalPlayers, setTotalPlayers] = useState(0);
  const navigate = useNavigate();
  const teamId = sessionStorage.getItem('selected_team_id');
  const playerId = sessionStorage.getItem('player_id');
  const isManager = authService.isManager();

  // Set default view mode to 'team' for managers
  useEffect(() => {
    if (isManager) {
      setViewMode('team');
    }
  }, [isManager]);

  useEffect(() => {
    const fetchTotalPlayers = async () => {
      try {
        if (!teamId || !isManager) return;
        
        const response = await api.get(`${process.env.REACT_APP_API_URL}/admin_get_players.php?team_id=${teamId}&filter=players`);
        if (response.data.success) {
          setTotalPlayers(response.data.total_players || 0);
        }
      } catch (error) {
        console.error('Error fetching total players:', error);
      }
    };

    fetchTotalPlayers();
  }, [teamId, isManager]);

  // Dummy training data function
  const getDummyTraining = () => {
    // Create an array of dummy training sessions
    const trainingSessions = [];
    
    // Get current date
    const today = new Date();

    const addSession = (idSuffix, date, response = 'Invited') => {
      trainingSessions.push({
        training_id: idSuffix,
        match_id: idSuffix,
        match_date: format(date, 'yyyy-MM-dd'),
        match_time: '18:00:00',
        time: '18:00:00',
        location: 'Claverham Community College',
        notes: 'Regular training session',
        response,
        total_yes: 7,
        total_no: 2,
        total_awaiting: 3,
        duration: 90,
        type: 'training',
        isDummy: true
      });
    };

    const getTuesdayInWeek = (baseDate) => {
      const d = new Date(baseDate);
      d.setDate(d.getDate() - ((d.getDay() + 7 - 2) % 7));
      return d;
    };

    const getThursdayInWeek = (baseDate) => {
      const d = new Date(baseDate);
      d.setDate(d.getDate() - ((d.getDay() + 7 - 4) % 7));
      return d;
    };

    // Past training sessions (last 2 weeks)
    for (let week = 1; week <= 2; week++) {
      const baseDate = new Date(today);
      baseDate.setDate(today.getDate() - (week * 7));
      addSession(`training-past-tuesday-${week}`, getTuesdayInWeek(baseDate), 'Yes');
      addSession(`training-past-thursday-${week}`, getThursdayInWeek(baseDate), 'Yes');
    }
    
    // Generate training sessions for the next 4 weeks (Tuesdays and Thursdays)
    for (let week = 0; week < 4; week++) {
      // Tuesday training (day 2 of week, 0-indexed)
      const tuesdayDate = new Date(today);
      tuesdayDate.setDate(today.getDate() + (2 - today.getDay() + 7) % 7 + (week * 7));
      
      // Thursday training (day 4 of week, 0-indexed)
      const thursdayDate = new Date(today);
      thursdayDate.setDate(today.getDate() + (4 - today.getDay() + 7) % 7 + (week * 7));
      
      addSession(`training-tuesday-${week + 1}`, tuesdayDate);
      addSession(`training-thursday-${week + 1}`, thursdayDate);
    }
    
    return trainingSessions;
  };

  // Fetch repeat training sessions
  const fetchRepeatSessions = useCallback(async () => {
    if (!isManager) return; // Only managers can see repeat sessions
    
    try {
      const response = await api.get(`${process.env.REACT_APP_API_URL}/get_repeat_training_sessions.php?team_id=${teamId}`);
      const result = response.data;
      
      if (result.success) {
        setRepeatSessions(result.sessions || []);
      } else {
        console.error('Failed to fetch repeat training sessions');
        setRepeatSessions([]);
      }
    } catch (error) {
      console.error('Error fetching repeat training sessions:', error);
      setRepeatSessions([]);
    }
  }, [teamId, isManager]);

  // Define fetchTrainingSessions with useCallback to prevent dependency loop
  const fetchTrainingSessions = useCallback(async () => {
    try {
      setLoading(true);
      // Build the API URL with player_id for responder information
      let apiUrl = `${process.env.REACT_APP_API_URL}/get_training_sessions.php?team_id=${teamId}`;
      
      // Add player_id parameter for responder information
      if (playerId) {
        apiUrl += `&child_id=${playerId}`;
      }
      
      const response = await api.get(apiUrl);
      const result = response.data;
      
      if (result.success) {
        // Process training sessions to ensure they have all required properties
        const processedSessions = result.sessions.map(session => ({
          ...session,
          response: session.response || 'Invited',
          total_yes: session.total_yes || 0,
          total_no: session.total_no || 0,
          response_updated: session.response_updated,
          responder_first_name: session.responder_first_name,
          responder_last_name: session.responder_last_name,
          onRespond: (response, isWaitlist) => handleRespond(session.match_id, response, isWaitlist),
          type: 'training'
        }));
        
        // Store all sessions (upcoming and past); split in render for tabs
        if (processedSessions.length === 0) {
          console.log('No training sessions found, showing dummy data');
          const dummySessions = getDummyTraining();
          setTrainingSessions(dummySessions);
          setShowDummyData(true);
        } else {
          setTrainingSessions(processedSessions);
          setShowDummyData(false);
        }
      } else {
        console.error('Failed to fetch training sessions');
        // Show dummy data on error
        const dummySessions = getDummyTraining();
        setTrainingSessions(dummySessions);
        setShowDummyData(true);
      }
    } catch (error) {
      console.error('Error fetching training sessions:', error);
      // Show dummy data on error
      const dummySessions = getDummyTraining();
      setTrainingSessions(dummySessions);
      setShowDummyData(true);
    } finally {
      setLoading(false);
    }
  }, [teamId]); // Only depends on teamId

  useEffect(() => {
    fetchTrainingSessions();
    fetchRepeatSessions();
  }, [viewMode, playerId, fetchTrainingSessions, fetchRepeatSessions]);

  const handleRespond = async (matchId, response, isWaitlist) => {
    try {
      const playerId = sessionStorage.getItem('player_id');
      const res = await api.post(`${process.env.REACT_APP_API_URL}/update_player_response.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          player_id: playerId,
          response: response,
          match_id: matchId,
          is_waitlist: isWaitlist ? 1 : 0
        })
      });

      const data = res.data;
      
      if (data.success) {
        // Update the local state to reflect the change
        setTrainingSessions(prevSessions => 
          prevSessions.map(session => 
            session.match_id === matchId 
              ? { 
                  ...session, 
                  response: response.charAt(0).toUpperCase() + response.slice(1),
                  response_updated: new Date().toISOString(),
                  responder_first_name: sessionStorage.getItem('first_name') || 'Parent',
                  responder_last_name: sessionStorage.getItem('last_name') || ''
                }
              : session
          )
        );
      } else {
        console.error('Failed to update response:', data.message);
      }
    } catch (error) {
      console.error('Error updating response:', error);
    }
  };

  const handleTrainingDeleted = (matchId) => {
    setTrainingSessions(currentSessions => 
      currentSessions.filter(session => session.match_id !== matchId)
    );
  };

  const handleEditRepeat = (repeatSession) => {
    setEditingRepeat(repeatSession);
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingRepeat(null);
  };

  const handleSaveRepeat = async (updatedData) => {
    try {
      const response = await api.post(`${process.env.REACT_APP_API_URL}/update_repeat_training.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          repeat_id: editingRepeat.repeat_id,
          ...updatedData
        })
      });

      const result = response.data;
      if (result.success) {
        // Refresh repeat sessions
        fetchRepeatSessions();
        handleCloseEditModal();
      } else {
        console.error('Failed to update repeat training:', result.message);
      }
    } catch (error) {
      console.error('Error updating repeat training:', error);
    }
  };

  const handleDeleteRepeat = async (repeatSession) => {
    const isConfirmed = window.confirm(
      'Delete this weekly training?\n\nAll upcoming sessions from today will be removed. Past sessions will be kept.\n\nThis cannot be undone.'
    );
    
    if (!isConfirmed) {
      return;
    }

    try {
      const response = await api.post(`${process.env.REACT_APP_API_URL}/delete_repeat_training.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          repeat_id: repeatSession.repeat_id
        })
      });

      const result = response.data;
      if (result.success) {
        fetchRepeatSessions();
        const deletedIds = new Set((result.deleted_match_ids || []).map(String));
        if (deletedIds.size > 0) {
          setTrainingSessions(currentSessions =>
            currentSessions.filter(session => !deletedIds.has(String(session.match_id)))
          );
        }
        const deletedCount = result.deleted_sessions || 0;
        const sessionLabel = deletedCount === 1 ? 'session was' : 'sessions were';
        alert(
          deletedCount > 0
            ? `Weekly training deleted. ${deletedCount} upcoming ${sessionLabel} removed.`
            : 'Weekly training deleted. There were no upcoming sessions to remove.'
        );
      } else {
        alert('Failed to delete weekly training: ' + result.message);
      }
    } catch (error) {
      console.error('Error deleting weekly training:', error);
      alert('An error occurred while deleting the weekly training');
    }
  };

  if (loading) {
    return <div className="loading">Loading training sessions...</div>;
  }

  // Remove duplicate sessions based on match_id
  const uniqueSessions = Array.from(new Map(
    trainingSessions.map(session => [session.match_id, session])
  ).values());

  const now = new Date();
  const isFutureSession = (session) => {
    const sessionDateTime = new Date(`${session.match_date} ${session.match_time}`);
    return sessionDateTime > now;
  };

  const upcomingTrainingSessions = uniqueSessions
    .filter(isFutureSession)
    .sort((a, b) =>
      new Date(`${a.match_date} ${a.match_time}`) - new Date(`${b.match_date} ${b.match_time}`)
    );

  const pastTrainingSessions = uniqueSessions
    .filter(session => !isFutureSession(session))
    .sort((a, b) =>
      new Date(`${b.match_date} ${b.match_time}`) - new Date(`${a.match_date} ${a.match_time}`)
    );

  const renderTrainingList = (sessions) => (
    <div className="matches-list">
      {sessions.map((session) => (
        <div key={session.match_id} className="match-wrapper">
          <TrainingCard
            training={session}
            onTrainingDeleted={handleTrainingDeleted}
          />
        </div>
      ))}
    </div>
  );

  return (
    <div className="matches-container training-schedule container">
      <Header />
      <h2 className="audiowide">Training Sessions</h2>
      
      {showDummyData && (
        <div className="dummy-data-notice global-notice">
          <h4>Welcome to FirstWhistle!</h4>
          <p>We're showing example training sessions to help you experience the app.
          Your upcoming and past training sessions will appear here once they've been scheduled by your coach.</p>
        </div>
      )}
      
      <div className="filters">
        <div className="fixtures-filter">
          <button
            className={`filter-button ${!showPastTraining ? 'active' : ''}`}
            onClick={() => setShowPastTraining(false)}
          >
            Upcoming Training
          </button>
          <button
            className={`filter-button ${showPastTraining ? 'active' : ''}`}
            onClick={() => setShowPastTraining(true)}
          >
            Previous Training
          </button>
        </div>

        {isManager && totalPlayers >= 6 && !showPastTraining && (
          <div className="create-match-section">
            <button
              type="button"
              className="create-match-button"
              onClick={() => navigate('/createTrainingSession')}
            >
              Create Training Session
            </button>
          </div>
        )}
      </div>

      {/* Repeat Training Sessions Accordion - Only for Managers */}
      {isManager && (
        <div className="repeat-sessions-accordion">
          <button
            type="button"
            className="accordion-header"
            onClick={() => setShowRepeatAccordion(!showRepeatAccordion)}
            aria-expanded={showRepeatAccordion}
          >
            <h3>Repeat Training Sessions ({repeatSessions.length})</h3>
            {showRepeatAccordion ? <FaChevronUp /> : <FaChevronDown />}
          </button>
          
          {showRepeatAccordion && (
            <div className="accordion-content">
              {repeatSessions.length > 0 ? (
                <div className="repeat-sessions-list">
                  {repeatSessions.map((session) => {
                    const dayName = format(new Date(session.match_date), 'EEEE');
                    const time = session.match_time ? session.match_time.substring(0, 5) : '';
                    return (
                      <div key={session.repeat_id} className="repeat-session-card">
                        <div className="repeat-session-card-header">
                          <h4>Every {dayName}</h4>
                          <span className={`status ${session.is_active ? 'active' : 'inactive'}`}>
                            {session.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <p className="repeat-session-meta">
                          {time}
                          {session.duration ? ` · ${session.duration} mins` : ''}
                        </p>
                        {session.location && (
                          <p className="repeat-session-location">{session.location}</p>
                        )}
                        <div className="action-buttons">
                          <button
                            type="button"
                            className="edit-button"
                            onClick={() => handleEditRepeat(session)}
                          >
                            <FaEdit /> Edit
                          </button>
                          <button
                            type="button"
                            className="delete-button"
                            onClick={() => handleDeleteRepeat(session)}
                          >
                            <FaTrash /> Delete
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="repeat-sessions-empty">
                  <p>No repeat training sessions found.</p>
                  <p>Create a training session with Repeat Weekly enabled to see it here.</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {!showPastTraining && upcomingTrainingSessions.length > 0 && (
        <>
          <h2 className="matches-heading">Upcoming Training</h2>
          {renderTrainingList(upcomingTrainingSessions)}
        </>
      )}

      {showPastTraining && pastTrainingSessions.length > 0 && (
        <>
          <h2 className="matches-heading">Previous Training</h2>
          {renderTrainingList(pastTrainingSessions)}
        </>
      )}

      {((showPastTraining && pastTrainingSessions.length === 0) ||
        (!showPastTraining && upcomingTrainingSessions.length === 0)) && (
        <div className="no-matches">
          <p>No {showPastTraining ? 'previous' : 'upcoming'} training sessions scheduled</p>
        </div>
      )}

      <ProFeatureModal
        isOpen={showProModal}
        onClose={() => setShowProModal(false)}
        featureName="Training History"
      />

      {/* Edit Repeat Training Modal */}
      {showEditModal && editingRepeat && (
        <div className="modal-overlay">
          <div className="modal">
            <button className="modal-close" onClick={handleCloseEditModal}>×</button>
            <h2>Edit Repeat Training Session</h2>
              <form onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                handleSaveRepeat({
                  location: formData.get('location'),
                  match_time: formData.get('match_time'),
                  duration: parseInt(formData.get('duration')),
                  instructions: formData.get('instructions'),
                  repeat_end_date: formData.get('repeat_end_date') || null,
                  is_active: formData.get('is_active') === 'on'
                });
              }}>
                <div className="form-group">
                  <label htmlFor="location">Location:</label>
                  <input
                    type="text"
                    id="location"
                    name="location"
                    defaultValue={editingRepeat.location}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="match_time">Time:</label>
                  <input
                    type="time"
                    id="match_time"
                    name="match_time"
                    defaultValue={editingRepeat.match_time}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="duration">Duration (minutes):</label>
                  <input
                    type="number"
                    id="duration"
                    name="duration"
                    defaultValue={editingRepeat.duration}
                    min="1"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="instructions">Instructions:</label>
                  <textarea
                    id="instructions"
                    name="instructions"
                    defaultValue={editingRepeat.instructions}
                    rows="3"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="repeat_end_date">End Date (Optional):</label>
                  <input
                    type="date"
                    id="repeat_end_date"
                    name="repeat_end_date"
                    defaultValue={editingRepeat.repeat_end_date || ''}
                  />
                </div>
                
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="is_active"
                      defaultChecked={editingRepeat.is_active}
                    />
                    Active (uncheck to disable this repeat series)
                  </label>
                </div>
                
                <div className="modal-buttons">
                  <button type="button" onClick={handleCloseEditModal} className="cancel">
                    Cancel
                  </button>
                  <button type="submit">
                    Save Changes
                  </button>
                </div>
              </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainingSchedule; 